/**
 * variant-lock.js (Phase 4.7, Revision 6)
 *
 * Liest URL-Parameter `?size=L&marke=BMW&reihe=3er&baujahr=2020` und wählt im
 * Variant-Picker EINMALIG die empfohlene Größe vor. Zeigt dazu einen kleinen
 * grünen Hinweis mit dem Fahrzeug. Andere Größen bleiben FREI wählbar — kein
 * Sperren, kein „Andere Größe wählen"-Button (Wunsch David, 2026-06-15).
 *
 * URL-Format (lesbar, cachebar, manuell editierbar):
 *   /products/car-perfect-stretch?variant=12345&size=L&marke=BMW&reihe=3er&baujahr=2020
 *
 * `size` ist der einzige Pflicht-Parameter. Andere sind optional (nur für den Hint).
 * Direkt-Visits ohne `?size` → kein Hint, normaler Picker.
 *
 * Robustheit gegen Shopify-Verhalten:
 *  1. scripts.js#updateURL setzt window.history.replaceState(…, `${base}?variant=<id>`),
 *     was unsere Cascade-Params wegwirft. → Wir patchen replaceState so, dass die
 *     "sticky" Cascade-Params auf demselben Pfad erhalten bleiben (sonst
 *     verschwindet der Hinweis, sobald der Kunde die Farbe wechselt).
 *  2. handleSwapProduct / view-transition ersetzt den Section-DOM. Unser Hint
 *     verschwindet. → MutationObserver re-rendert den Hint, sobald die neue
 *     Section gerendert ist. Die Größen-Vorauswahl passiert dabei NICHT erneut
 *     (state.preselected), damit eine vom Kunden gewählte Größe bestehen bleibt.
 */
(() => {
  const STICKY = ["size", "marke", "modell", "reihe", "baujahr"];
  const state = (window.__cecVariantLockState =
    window.__cecVariantLockState || {
      booted: false,
      preselected: false,
    });

  // (1) replaceState-Patch — muss laufen, bevor irgendein Variant-Change Shopify zur
  // URL-Aktualisierung triggert. variant-lock.js wird mit defer geladen, also vor
  // user-getriebenen Variant-Changes.
  if (!window.__cecReplaceStatePatched) {
    const orig = window.history.replaceState;
    window.history.replaceState = function (state, title, url) {
      if (url != null) {
        try {
          const current = new URL(window.location.href);
          const next = new URL(url, window.location.origin);
          if (next.pathname === current.pathname) {
            STICKY.forEach((k) => {
              if (current.searchParams.has(k) && !next.searchParams.has(k)) {
                next.searchParams.set(k, current.searchParams.get(k));
              }
            });
            return orig.call(this, state, title, next.toString());
          }
        } catch (_) {
          /* fall through */
        }
      }
      return orig.call(this, state, title, url);
    };
    window.__cecReplaceStatePatched = true;
  }

  function readCtx() {
    const url = new URL(location.href);
    const size = url.searchParams.get("size");
    if (!size) return null;
    return {
      size,
      marke: url.searchParams.get("marke") || "",
      reihe:
        url.searchParams.get("reihe") || url.searchParams.get("modell") || "",
      baujahr: url.searchParams.get("baujahr") || "",
    };
  }

  function buildHintText(ctx) {
    const t = window.cecVariantLockStrings || {};
    const tpl =
      t.hint ||
      "Passend für Ihr Fahrzeug: __MARKE__ __REIHE__ (__BAUJAHR__). Empfohlene Größe: __SIZE__";
    return tpl
      .replace("__MARKE__", ctx.marke)
      .replace("__REIHE__", ctx.reihe)
      .replace("__BAUJAHR__", ctx.baujahr)
      .replace("__SIZE__", ctx.size)
      .replace(/\s+/g, " ")
      .replace("()", "")
      .trim();
  }

  function getTargets(root) {
    // Rolle statt Name — `data-option-name` ist auf /en "Size".
    const scope = root || document;
    const fieldsets = scope.querySelectorAll('fieldset[data-option-role="size"]');
    return Array.from(
      fieldsets.length
        ? fieldsets
        : scope.querySelectorAll('fieldset[data-option-name="Größe"]'),
    )
      .map((sizeFieldset) => ({
        sizeFieldset,
        productVariants: sizeFieldset.closest(".product-variants"),
        sizeInputs: Array.from(
          sizeFieldset.querySelectorAll('input[type="radio"]'),
        ),
      }))
      .filter((target) => target.productVariants && target.sizeInputs.length);
  }

  function ensureHint(productVariants, ctx) {
    let hint = productVariants.previousElementSibling;
    if (!hint?.classList?.contains("variant-lock-hint")) {
      hint = document.createElement("div");
      hint.className = "variant-lock-hint";
      hint.setAttribute("role", "status");
      productVariants.before(hint);
    }

    if (!hint.querySelector(".variant-lock-text")) {
      hint.innerHTML = '<strong class="variant-lock-text"></strong>';
    }

    const hintText = buildHintText(ctx);
    const textEl = hint.querySelector(".variant-lock-text");
    // Idempotent: nur schreiben wenn sich der Inhalt geändert hat. Jedes
    // unbedingte textContent= würde den Text-Knoten ersetzen und damit ein
    // childList-MutationRecord erzeugen — das fütterte den MutationObserver
    // mit Endlos-Wiederholungen von applyHint().
    if (textEl.textContent !== hintText) textEl.textContent = hintText;
    return hint;
  }

  function applyHint(root = document) {
    const ctx = readCtx();
    if (!ctx) return false;

    const targets = getTargets(root);
    if (!targets.length) return false;

    targets.forEach(({ productVariants, sizeInputs }) => {
      ensureHint(productVariants, ctx);

      // Empfohlene Größe EINMALIG vorauswählen. Andere Größen bleiben aktiv
      // (frei wählbar). Nach der ersten Vorauswahl nie wieder eingreifen, damit
      // ein Section-Swap die Kundenwahl nicht überschreibt.
      if (!state.preselected) {
        const match = sizeInputs.find((btn) => btn.value === ctx.size);
        if (match && !match.checked) {
          match.checked = true;
          match.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    });

    state.preselected = true;
    return true;
  }

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  window.__cecApplyVariantLock = applyHint;

  ready(() => {
    if (state.booted) {
      applyHint();
      return;
    }
    state.booted = true;

    applyHint();

    // (2) Section-Swap überleben: sobald der Variant-Picker nach einem
    // Shopify-Re-Render wieder im DOM steht (und unser Hint fehlt), neu rendern.
    const target = document.querySelector("main") || document.body;
    if (!target) return;
    let pending = false;
    const obs = new MutationObserver(() => {
      if (pending) return;
      pending = true;
      // Microtask-Debounce: viele Mutations pro Swap → einmal anwenden
      queueMicrotask(() => {
        pending = false;
        window.__cecApplyVariantLock();
      });
    });
    obs.observe(target, { childList: true, subtree: true });

    // Theme-Editor / Section-Reload
    document.addEventListener("shopify:section:load", (event) => {
      window.__cecApplyVariantLock(event.target || document);
    });
    document.addEventListener("shopify:section:unload", (event) => {
      event.target
        ?.querySelectorAll?.(".variant-lock-hint")
        .forEach((hint) => hint.remove());
    });
  });
})();
